import { ThinkingBlock } from "@/wizard/ThinkingBlock"
import { marked } from "marked"
import {
  create_ledger_account,
  recode_deduction_account,
  set_backup_from_user_file,
  type AccountingWizardOutcome,
  type AccountingWizardPath,
  type AppliedLedgerAccount,
  type LedgerWizAppliedFields,
} from "@/api/accounting_wizard"
import { AutocompleteSelect, type SelectItem as AccountSelectItem } from "@/accounting/autocomplete_select"
import { LedgerAccounts, loadAccountCodes } from "@/accounting/accounts"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast.ts"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { useEffect, useState } from "preact/hooks"

const PATH_LABELS: Record<AccountingWizardPath, string> = {
  no_backup_rule_auto_code: "Rule auto-code (no AI)",
  no_backup_no_rule: "No backup / no rule",
  no_backup_gemini: "Coded from context (no backup)",
  needs_backup: "Needs backup",
  unanimous_no_gemini: "Unanimous rule — no model call",
  gemini_full_list: "AI — picked from full account list",
  gemini_locked_reason_code: "AI — locked account + extraction",
  manual_recode: "Manually re-coded",
  error: "Error",
}

marked.setOptions({ breaks: true, gfm: true })

function LedgerWizAppliedFieldsBlock({ applied }: { applied: LedgerWizAppliedFields }) {
  return (
    <div class="rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-3 text-sm">
      <p class="text-xs font-semibold uppercase tracking-wide text-slate-600">Execution date &amp; retailer</p>
      <div class="space-y-1">
        <p class="font-medium text-slate-900">Execution date</p>
        <p class="text-slate-800">{applied.execution_date ? applied.execution_date : "— (not set this run)"}</p>
        <p class="text-xs text-slate-600 leading-snug">{applied.execution_date_note}</p>
      </div>
      <div class="space-y-1 border-t border-slate-200/80 pt-3">
        <p class="font-medium text-slate-900">Retailer</p>
        <p class="text-slate-800">{applied.retailer_name ? applied.retailer_name : "— (not set this run)"}</p>
        <p class="text-xs text-slate-600 leading-snug">{applied.retailer_note}</p>
      </div>
    </div>
  )
}

export interface AccountingWizardPanelProps {
  outcome: AccountingWizardOutcome | null
  isLoading?: boolean
  /** Latest progress line from streamed `/run-stream` (e.g. loading backup, calling AI). */
  loading_status_message?: string | null
  /** Accumulated model thinking text while streaming. */
  streaming_thinking?: string
  /** True while thought tokens may still arrive. */
  thinking_streaming?: boolean
  className?: string
  /** After promoting a user file to official backup; e.g. re-run the wizard for this deduction. */
  onAfterSetBackup?: (deduction_id: string) => void
  /** After the user manually re-codes to a different/added ledger account. */
  onRecoded?: (outcome: AccountingWizardOutcome) => void
  hasCachedOutcome?: boolean
}

/** Error codes CodingWiz returns, in words a user can act on. Unknown codes are shown as-is. */
export function wizard_error_text(code: string): string {
  switch (code) {
    case "needs_backup":
      return "Not coded: attach backup (or promote an uploaded file below), then run CodingWiz again."
    case "no_backup_no_rule":
      return "Not coded: no backup and no category rule. Run CodingWiz again to code from context."
    default:
      return code
  }
}

export function AccountingWizardPanel({
  outcome,
  isLoading,
  loading_status_message,
  streaming_thinking,
  thinking_streaming,
  className,
  onAfterSetBackup,
  onRecoded,
  hasCachedOutcome,
}: AccountingWizardPanelProps) {
  const candidates = outcome?.user_files_as_backup_candidates
  const first_uri = candidates?.[0]?.s3_uri ?? ""
  const [picked_uri, set_picked_uri] = useState(first_uri)
  const [setting_backup, set_setting_backup] = useState(false)

  useEffect(() => {
    set_picked_uri(first_uri)
  }, [outcome?.deduction_id, first_uri])

  async function handle_use_as_backup() {
    if (!outcome || !candidates?.length || !picked_uri) {
      return
    }
    set_setting_backup(true)
    const res = await set_backup_from_user_file(outcome.deduction_id, picked_uri)
    set_setting_backup(false)
    if (res.ok) {
      toast({
        title: "Backup set",
        description: "This file is now the official backup. Re-running CodingWiz…",
      })
      RefreshFlag.set((f: boolean) => !f)
      onAfterSetBackup?.(outcome.deduction_id)
    } else {
      toast({
        title: "Could not set backup",
        description: res.error.message ?? "Request failed",
        variant: "destructive",
      })
    }
  }

  if (isLoading) {
    return (
      <div class={`space-y-3 text-sm ${className ?? ""}`}>
        {loading_status_message ? (
          <p class="text-gray-700 flex items-start gap-2">
            <span
              class="mt-1.5 inline-block h-2 w-2 rounded-full bg-primary animate-pulse shrink-0"
              aria-hidden
            />
            <span>{loading_status_message}</span>
          </p>
        ) : (
          <p class="text-gray-500">Running CodingWiz…</p>
        )}
        {streaming_thinking && streaming_thinking.length > 0 ? (
          <ThinkingBlock
            content={streaming_thinking}
            isStreaming={!!thinking_streaming}
            defaultExpanded={true}
          />
        ) : null}
      </div>
    )
  }

  if (!outcome) {
    return null
  }

  const pathLabel = PATH_LABELS[outcome.path] ?? outcome.path
  const primary_bullet = outcome.summary_bullets[0]
  const detail_bullets = outcome.summary_bullets.slice(1)
  const summaryHtml = marked.parse(outcome.summary_markdown || "") as string

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <div class="flex flex-wrap items-center gap-2">
        <span
          class={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${
            outcome.success ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"
          }`}>
          {pathLabel}
        </span>
        {!outcome.success && outcome.error ? (
          <span class="text-xs text-red-600 truncate max-w-full" title={outcome.error}>
            {wizard_error_text(outcome.error)}
          </span>
        ) : null}
      </div>

      {hasCachedOutcome ? (
        <p class="text-xs text-gray-500">Showing previous ledger coding result.</p>
      ) : null}

      <LedgerAccountOverride
        deduction_id={outcome.deduction_id}
        suggested={outcome.applied_account}
        onRecoded={onRecoded}
      />

      {/* The account is shown (and editable) in the picker above, so only show the
          first summary line when it's an explanation rather than the chosen account. */}
      {!outcome.applied_account && primary_bullet ? <p class="text-sm text-gray-900">{primary_bullet}</p> : null}

      {outcome.applied_fields ? <LedgerWizAppliedFieldsBlock applied={outcome.applied_fields} /> : null}

      {candidates && candidates.length > 0 ? (
        <div class="rounded-md border border-amber-200 bg-amber-50/80 p-3 space-y-2">
          <p class="text-sm font-medium text-amber-950">No official backup yet</p>
          <p class="text-xs text-amber-900/90">
            If one of your uploaded files is the backup document, set it here so CodingWiz can read it for coding.
          </p>
          <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Select value={picked_uri} onValueChange={set_picked_uri}>
              <SelectTrigger class="w-full sm:max-w-md bg-white">
                <SelectValue placeholder="Choose a file" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.s3_uri} value={c.s3_uri}>
                    {c.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              disabled={setting_backup || !picked_uri}
              onClick={() => void handle_use_as_backup()}>
              {setting_backup ? "Saving…" : "Use as backup & re-run"}
            </Button>
          </div>
        </div>
      ) : null}

      {(detail_bullets.length > 0 || outcome.summary_markdown || outcome.model_reasoning) && (
        <details class="border-t pt-2">
          <summary class="cursor-pointer text-sm font-medium text-gray-700">Show details &amp; reasoning</summary>
          <div class="mt-3 space-y-3">
            {detail_bullets.length > 0 ? (
              <ul class="list-disc pl-5 space-y-1 text-sm text-gray-800">
                {detail_bullets.map((b: string, i: number) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            ) : null}
            {outcome.summary_markdown ? (
              <div class="prose prose-sm max-w-none border-t pt-3" dangerouslySetInnerHTML={{ __html: summaryHtml }} />
            ) : null}
            {outcome.model_reasoning ? (
              <div class="border-t pt-2">
                <p class="text-xs font-medium text-gray-500 mb-1">Model reasoning</p>
                <ThinkingBlock content={outcome.model_reasoning} isStreaming={false} defaultExpanded={false} />
              </div>
            ) : null}
          </div>
        </details>
      )}
    </div>
  )
}

interface LedgerAccountOverrideProps {
  deduction_id: string
  /** The account CodingWiz applied — pre-fills the picker for editing. */
  suggested?: AppliedLedgerAccount | null
  onRecoded?: (outcome: AccountingWizardOutcome) => void
}

function applied_to_select_item(a?: AppliedLedgerAccount | null): AccountSelectItem | undefined {
  if (!a) return undefined
  return { id: a.id, name: a.expense_account, expense_account: a.expense_account, account_number: a.account_number }
}

/**
 * "Wrong account?" control: pick any org ledger account with the same picker used
 * across coding — pre-filled with the account the AI suggested — or add one that
 * doesn't exist yet, and re-code this deduction to it. Replaces the old workaround
 * of re-running CodingWiz with a free-text "add <account>" instruction.
 */
function LedgerAccountOverride({ deduction_id, suggested, onRecoded }: LedgerAccountOverrideProps) {
  const accounts = LedgerAccounts.use()
  const [selected, set_selected] = useState<AccountSelectItem | undefined>(applied_to_select_item(suggested))
  const [applying, set_applying] = useState(false)
  const [adding, set_adding] = useState(false)
  const [new_number, set_new_number] = useState("")
  const [new_name, set_new_name] = useState("")
  const [creating, set_creating] = useState(false)

  // Load the org's ledger accounts once (shared store used across coding).
  useEffect(() => {
    if (accounts.length === 0) {
      void loadAccountCodes()
    }
  }, [accounts.length])

  // Pre-fill with the AI's suggested account when it changes (new run).
  useEffect(() => {
    set_selected(applied_to_select_item(suggested))
  }, [suggested?.id])

  async function handle_create_account() {
    if (!new_number.trim() || !new_name.trim()) {
      toast({ title: "Missing fields", description: "Enter both an account number and a name.", variant: "destructive" })
      return
    }
    set_creating(true)
    const res = await create_ledger_account(new_number, new_name)
    set_creating(false)
    if (res.ok) {
      const created = res.value.data
      await loadAccountCodes()
      set_selected({
        id: created.id,
        name: created.expense_account,
        expense_account: created.expense_account,
        account_number: created.account_number,
      })
      set_adding(false)
      set_new_number("")
      set_new_name("")
      toast({ title: "Account added", description: `${created.account_number} — ${created.expense_account}` })
    } else {
      toast({ title: "Could not add account", description: res.error.message ?? "Request failed", variant: "destructive" })
    }
  }

  async function handle_apply() {
    if (!selected) {
      return
    }
    set_applying(true)
    const res = await recode_deduction_account(deduction_id, selected.id)
    set_applying(false)
    if (res.ok) {
      toast({ title: "Re-coded", description: "This deduction was re-coded to the selected account." })
      RefreshFlag.set((f: boolean) => !f)
      onRecoded?.(res.value.data)
    } else {
      toast({ title: "Could not re-code", description: res.error.message ?? "Request failed", variant: "destructive" })
    }
  }

  return (
    <div class="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 space-y-2">
      <p class="text-sm font-medium text-indigo-950">Ledger account</p>
      <p class="text-xs text-indigo-900/80">
        Pre-filled with CodingWiz’s pick — change it or add a new account, then Apply to re-code.
      </p>
      <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div class="w-full sm:max-w-md">
          <AutocompleteSelect
            values={accounts}
            initial={selected}
            insideModal={true}
            onSelect={(item: AccountSelectItem) => set_selected(item)}
          />
        </div>
        <Button type="button" size="sm" disabled={applying || !selected} onClick={() => void handle_apply()}>
          {applying ? "Applying…" : "Apply"}
        </Button>
      </div>

      {adding ? (
        <div class="rounded border border-indigo-200 bg-white p-2 space-y-2">
          <p class="text-xs font-medium text-gray-700">Add a new ledger account</p>
          <div class="flex flex-col sm:flex-row gap-2">
            <Input
              class="text-sm"
              placeholder="Account number (e.g. 5061)"
              value={new_number}
              onInput={(e: Event) => set_new_number((e.target as HTMLInputElement).value)}
            />
            <Input
              class="text-sm"
              placeholder="Name (e.g. Digital Marketing Tradespend USA)"
              value={new_name}
              onInput={(e: Event) => set_new_name((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="flex gap-2">
            <Button type="button" size="sm" disabled={creating} onClick={() => void handle_create_account()}>
              {creating ? "Adding…" : "Create & select"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => set_adding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => set_adding(true)}
          class="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline">
          + Add a ledger account that isn't listed
        </button>
      )}
    </div>
  )
}
